import { history, historyField } from "@codemirror/commands";
import {
  EditorState as CMEditorState,
  EditorSelection,
} from "@codemirror/state";
import { createContext, useCallback, useEffect, useRef, useState } from "react";
import { getQueryList } from "utils/helpers/Query";
import { v4 as uuidv4 } from "uuid";

import { useEnableGcpAccount } from "featureFlags/hooks/useEnableGcpAccount";
import { useFirstTimeExperience } from "hooks/useFirstTimeExperience";
import createPersistedReducer from "hooks/usePersistedReducer";
import { MyAccount } from "services/account/account.types";
import { useDatabasesNames } from "services/databases/useDatabasesNames";
import { useWorkspaceEngines } from "services/engines/useWorkspaceEngines";
import { getFireboltCoreConfig } from "services/liteRuntimeConfig";

import { documentsReducer } from "pages/DevelopWorkspace/contexts/DocumentsContext/documentsReducer";
import { getExecutionStartError } from "pages/DevelopWorkspace/contexts/DocumentsContext/helpers/getExecutionStartError";
import normalizeDocumentsRawState from "pages/DevelopWorkspace/contexts/DocumentsContext/helpers/normalizeDocumentsRawState";
import { persistFilter } from "pages/DevelopWorkspace/contexts/DocumentsContext/helpers/persistFilter";
import {
  ScriptStatus,
  useScripts,
} from "pages/DevelopWorkspace/contexts/ScriptsContext/ScriptsContext";
import { getDocumentScript } from "pages/DevelopWorkspace/helpers/getDocumentScript";
import {
  buildFixUserPrompt,
  callA2ALLM,
  generatePrompt,
} from "pages/DevelopWorkspace/helpers/llmUtils";
import {
  AiStateExternalInitializer,
  CancellationStatus,
  DocumentAiStateInitialData,
  DocumentAiStateUpdate,
  DocumentEngineMonitoring,
  DocumentLayout,
  DocumentOutputTab,
  DocumentsState,
  EditorState,
  Execution,
  ExecutionContext,
  ExecutionType,
  QueryStatement,
  QueryStatementResult,
  QueryStatementStatus,
  ScriptType,
  SortOrder,
  WorkspaceDocument,
} from "pages/DevelopWorkspace/workspace.types";

import { useLayoutToggles } from "components/LayoutToggles/context";
import {
  StatusMessagePosition,
  StatusMessageType,
} from "components/StatusMessageQueue/StatusMessageQueueProvider";
import useStatusMessageQueue from "components/StatusMessageQueue/hooks/useStatusMessageQueue";

import { FIRST_SCRIPT_TEMPLATE } from "../../scriptTemplates";
import { DocumentActionType } from "./actions.types";
import { getQueryStatementLocation } from "./helpers/getQueryStatementLocation";
import {
  SwitchActiveOutputPayload,
  SwitchActiveOutputTabCallback,
  SwitchDocumentActiveQueryStatementCallback,
  SwitchDocumentActiveQueryStatementPayload,
} from "./types";

type DocumentsContextType = {
  state: DocumentsState;
  qsResults: Record<string, QueryStatementResult | undefined>;
  shouldGenerateSampleScript: boolean;
  actions: {
    sortDocuments: (sortOrder: SortOrder) => void;
    createDocument: (
      {
        title,
        content,
        context,
      }: {
        title?: string;
        content?: string;
        context?: Partial<ExecutionContext>;
      },
      options?: {
        autoRun?: boolean;
      }
    ) => Promise<string>;
    setActiveDocument: (documentId: string) => void;
    removeDocument: (documentId: string) => void;
    removeAllDocuments: (exceptDocumentIds: string[]) => void;
    renameDocument: (documentId: string, newTitle: string) => void;
    updateDocumentContent: (
      documentId: string,
      editorState: EditorState
    ) => void;
    changeDocumentContext: (
      documentId: string,
      context: Partial<ExecutionContext>
    ) => void;
    startDocumentExecution: (options: {
      documentId: string;
      selectedContent: string;
      selectionOffset: number;
      lineNumber: number;
      executionType?: ExecutionType;
    }) => void;
    updateDocumentQueryStatement: (
      documentId: string,
      id: string,
      queryStatement: Partial<Omit<QueryStatement, "prevStatus">>,
      result?: QueryStatementResult
    ) => void;
    cancelDocumentExecution: (documentId: string) => void;
    updateDocumentCancellationStatus: (
      documentId: string,
      queryStatementId: string,
      status: CancellationStatus
    ) => void;
    switchDocumentActiveQueryStatement: SwitchDocumentActiveQueryStatementCallback;
    switchActiveOutputTab: SwitchActiveOutputTabCallback;
    updateDocumentEngineMonitoring: (
      documentId: string,
      engineMonitoring: DocumentEngineMonitoring
    ) => void;
    changeDocumentLayout: (
      documentId: string,
      layout: Partial<DocumentLayout>
    ) => void;
    reorderDocument: (documentId: string, overDocumentId: string) => void;
    fixQueryStatement: (
      documentId: string,
      queryStatementId: string,
      errorMessage: string
    ) => void;
    handleAIOperation: (documentId: string, userPrompt: string) => void;
    cancelAIOperation: (documentId: string) => void;
    updateDocumentAIState: (
      documentId: string,
      aiState: DocumentAiStateUpdate
    ) => void;
    addDocumentAIState: (
      documentId: string,
      aiState: DocumentAiStateInitialData
    ) => void;
    removeDocumentAIState: (documentId: string) => void;
    changeQueryStatementResultState: (options: Record<string, unknown>) => void;
  };
};

interface DocumentsContextProviderProps {
  children: React.ReactNode;
  account: MyAccount;
  loginId: string;
  organizationName: string;
}

export const DocumentsContext = createContext<DocumentsContextType | undefined>(
  undefined
);

export const INITIAL_STATE: DocumentsState = {
  documents: [],
  activeDocumentId: null,
};

export const DEFAULT_EXECUTION_CONTEXT = {
  engine: {
    name: process.env.REACT_APP_LITE
      ? getFireboltCoreConfig().defaultEngine
      : "system",
  },
  database: { name: "" },
  settings: {},
  machineGeneratedSettings: {},
};

export const DEFAULT_LAYOUT = {
  editorHeightPx: 500,
  activeOutputTab: DocumentOutputTab.Results,
  chatWidthPx: 500,
};

const usePersistedReducer = createPersistedReducer();

export const DocumentsContextProvider = (
  props: DocumentsContextProviderProps
) => {
  const { children, account, loginId, organizationName } = props;

  const { putStatusMessage } = useStatusMessageQueue();
  const enabledGcpAccount = useEnableGcpAccount();

  const aiAbortControllerByDocumentId = useRef<{
    [documentId: string]: AbortController | null;
  }>({});

  const { state: scriptsState, actions: scriptsActions } = useScripts();

  const { data: engines } = useWorkspaceEngines({ includeSystemEngine: true });
  const databases = useDatabasesNames();
  const { setLayout } = useLayoutToggles();

  const firstEngineNameInList = engines?.[0]?.engineName;
  const firstDatabaseNameInList = databases?.[0]?.catalogName;
  const qsResults = useRef<{
    [qsId: string]: QueryStatementResult | undefined;
  }>({});

  const [documentsState, dispatch] = usePersistedReducer(
    `FIREBOLT_documents|${account.id}`,
    documentsReducer,
    INITIAL_STATE,
    persistFilter,
    normalizeDocumentsRawState
  );
  // Store latest documents state to ensure AI operations use up-to-date info (e.g., for pos or panel changes)
  const documentsStateRef = useRef(documentsState);
  useEffect(() => {
    documentsStateRef.current = documentsState;
  }, [documentsState]);

  // Store latest scripts state so AI callbacks always apply changes to the most recent editor state
  const scriptsStateRef = useRef(scriptsState);
  useEffect(() => {
    scriptsStateRef.current = scriptsState;
  }, [scriptsState]);

  useEffect(() => {
    return () => {
      // clean up results on unmount
      qsResults.current = {};
      // abort all ongoing AI operations
      Object.values(aiAbortControllerByDocumentId.current).forEach(
        controller => {
          controller?.abort();
        }
      );
      aiAbortControllerByDocumentId.current = {};
    };
  }, []);

  useEffect(() => {
    // garbage collection of results
    const existinQsIds = new Set<string>();
    documentsState.documents.forEach(document => {
      document.execution?.queryStatements.forEach(queryStatement => {
        existinQsIds.add(queryStatement.id);
      });
    });

    Object.keys(qsResults.current).forEach(qsId => {
      if (!existinQsIds.has(qsId)) {
        delete qsResults.current[qsId];
      }
    });
  });

  useEffect(() => {
    Object.entries(aiAbortControllerByDocumentId.current).forEach(
      ([documentId, controller]) => {
        const document = documentsState.documents.find(
          doc => doc.id === documentId
        );
        if (!document) {
          controller?.abort();
          delete aiAbortControllerByDocumentId.current[documentId];
        }
      }
    );
  }, [documentsState]);

  const { shouldGenerateSampleScript, onSampleScriptGenerated } =
    useFirstTimeExperience({
      loginId,
      organizationName,
      hasExistingScripts: !!documentsState.documents.length,
    });

  const [documentsIdsToRun, setDocumentsIdsToRun] = useState<string[]>([]);

  useEffect(() => {
    // remove document if script has error

    documentsState.documents.forEach(document => {
      const scriptState = scriptsState.localScripts[document.script.id];

      if (scriptState && scriptState.status === ScriptStatus.error) {
        dispatch({
          type: DocumentActionType.REMOVE_DOCUMENT,
          payload: document.id,
        });
      }
    });
  }, [scriptsState.localScripts, documentsState.documents, dispatch]);

  useEffect(() => {
    // check if selected engine is available, otherwise change to system
    if (engines) {
      documentsState.documents.forEach(document => {
        const engine = engines.find(
          engine => engine.engineName === document.context.engine?.name
        );

        if (!engine) {
          dispatch({
            type: DocumentActionType.CHANGE_DOCUMENT_CONTEXT,
            payload: {
              documentId: document.id,
              context: {
                engine: {
                  name: process.env.REACT_APP_LITE
                    ? getFireboltCoreConfig().defaultEngine
                    : "system",
                },
              },
            },
          });
        }
      });
    }

    // check if selected database is available, otherwise change to none
    if (databases) {
      documentsState.documents.forEach(document => {
        const database = databases.find(
          database => database.catalogName === document.context.database.name
        );

        if (!database) {
          dispatch({
            type: DocumentActionType.CHANGE_DOCUMENT_CONTEXT,
            payload: {
              documentId: document.id,
              context: {
                database: { name: "" },
              },
            },
          });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only depends on engines and databases
  }, [engines, databases]);

  useEffect(() => {
    documentsState.documents.forEach(document => {
      const scriptStorage =
        document.script.type === ScriptType.local
          ? scriptsState.localScripts
          : scriptsState.remoteScripts;

      if (!scriptStorage[document.script.id]) {
        scriptsActions.loadScript(document.script.id, document.script.type);
      }
    });
  }, [
    documentsState,
    scriptsActions,
    scriptsState.localScripts,
    scriptsState.remoteScripts,
  ]);

  const initDocumentExecution = async (
    documentId: string,
    selectedContent: string,
    executionType: ExecutionType,
    selectionOffset: number,
    lineNumber: number
  ) => {
    const document = documentsState.documents.find(
      document => document.id === documentId
    );

    if (!document) {
      console.error("Document not found");
      return;
    }

    if (!engines) {
      console.error(
        `Engines not found (looking for: ${document.context.engine.name})`
      );
      return;
    }

    document.execution?.queryStatements.forEach(queryStatement => {
      delete qsResults.current[queryStatement.id];
    });

    const engine = engines.find(
      engine => engine.engineName === document.context.engine.name
    );

    if (!engine) {
      console.error(
        `Engine not found: ${document.context.engine.name}. Available engines: ${
          engines.map(e => e.engineName).join(", ") || "<none>"
        }`
      );
      return;
    }

    const documentExecutionError = !enabledGcpAccount
      ? getExecutionStartError(engine)
      : null;

    const sqlStatements = getQueryList(selectedContent);

    if (sqlStatements.length === 0) {
      // nothing to execute (probably only whitespace selected)
      return;
    }

    const queryStatements = sqlStatements.map(query => {
      const location = getQueryStatementLocation(
        lineNumber,
        selectionOffset,
        query
      );
      return {
        content: query.text,
        status: QueryStatementStatus.pending,
        error: null,
        id: "query_statement." + uuidv4(),
        responseStatusCode: null,
        statistics: null,
        location,
      };
    });

    const execution: Execution = {
      executionType,
      queryStatements,
      userSelectedActiveQueryStatementIndexTimestamp: 0,
      activeQueryStatementIndex: 0,
      executionTimestamp: Date.now(),
      documentExecutionError,
    };

    dispatch({
      type: DocumentActionType.INIT_DOCUMENT_EXECUTION,
      payload: {
        documentId,
        execution,
      },
    });

    setLayout(layout => {
      return {
        ...layout,
        results: {
          ...layout.results,
          expanded: true,
        },
      };
    });
  };

  const startDocumentExecution = ({
    documentId,
    selectedContent,
    selectionOffset,
    lineNumber,
    executionType = ExecutionType.Query,
  }: {
    documentId: string;
    selectedContent: string;
    selectionOffset: number;
    lineNumber: number;
    executionType?: ExecutionType;
  }) => {
    initDocumentExecution(
      documentId,
      selectedContent,
      executionType,
      selectionOffset,
      lineNumber
    );
  };

  useEffect(() => {
    /*
    Executes documents after their successful creation. If autoRun option was set.
     */
    if (documentsIdsToRun.length > 0) {
      documentsIdsToRun.forEach(documentId => {
        const document = documentsState.documents.find(
          document => document.id === documentId
        );
        if (document) {
          const script = getDocumentScript(document, scriptsState);

          if (!script) {
            return;
          }

          startDocumentExecution({
            documentId,
            selectedContent: script.editorState.doc,
            selectionOffset: 0,
            lineNumber: 1,
            executionType: ExecutionType.Query,
          });
        }
      });
      setDocumentsIdsToRun([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentsIdsToRun]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- it's ok here
  const createDocument = async (
    {
      title,
      content,
      context,
    }: {
      title?: string;
      content?: string;
      context?: Partial<ExecutionContext>;
    },
    options?: {
      autoRun?: boolean;
    }
  ) => {
    const newScriptId = `account.${account.id}.script.` + uuidv4();

    const newDocumentId = "document." + uuidv4();

    const newDocContext = context
      ? {
          ...DEFAULT_EXECUTION_CONTEXT,
          ...context,
        }
      : DEFAULT_EXECUTION_CONTEXT;

    const newDocument: WorkspaceDocument = {
      id: newDocumentId,
      script: {
        id: newScriptId,
        type: ScriptType.local,
      },
      context: newDocContext,
      execution: null,
      createdAt: Date.now(),
      aiState: null,
      engineMonitoring: {
        timeRange: null,
        utilChart: {
          highlightedKeys: [],
        },
        queriesChart: {
          highlightedKeys: [],
        },
      },
      layout: DEFAULT_LAYOUT,
    };

    dispatch({
      type: DocumentActionType.ADD_DOCUMENT,
      payload: newDocument,
    });

    const tempState = CMEditorState.create({
      doc: content || "",
      extensions: [history()],
    });

    const tempJson = tempState.toJSON({
      history: historyField,
    });
    const editorState: EditorState = {
      doc: tempJson.doc,
      selection: tempJson.selection,
      history: tempJson.history,
      original: null,
    };

    await scriptsActions.createScript(newScriptId, ScriptType.local, {
      title: title || `Script ${documentsState.documents.length + 1}`,
      editorState,
    });

    if (options?.autoRun) {
      setDocumentsIdsToRun(documentsIdsToRun => [
        ...documentsIdsToRun,
        newDocumentId,
      ]);
    }

    return newDocumentId;
  };

  useEffect(() => {
    // create new document if there are no documents
    if (documentsState.documents.length <= 0) {
      const content = shouldGenerateSampleScript ? FIRST_SCRIPT_TEMPLATE : "";
      const context = enabledGcpAccount
        ? {
            engine: { name: firstEngineNameInList ?? "" },
            database: { name: firstDatabaseNameInList ?? "" },
          }
        : undefined;
      createDocument({
        title: "Script 1",
        content,
        context,
      });
      if (shouldGenerateSampleScript) {
        onSampleScriptGenerated();
      }
    }
  }, [
    documentsState.documents.length,
    createDocument,
    shouldGenerateSampleScript,
    onSampleScriptGenerated,
    enabledGcpAccount,
    firstEngineNameInList,
    firstDatabaseNameInList,
  ]);

  const changeDocumentLayout = (
    documentId: string,
    layout: Partial<DocumentLayout>
  ) => {
    dispatch({
      type: DocumentActionType.CHANGE_DOCUMENT_LAYOUT,
      payload: {
        documentId,
        layout,
      },
    });
  };

  const sortDocuments = (sortOrder: SortOrder) => {
    dispatch({
      type: DocumentActionType.SORT_DOCUMENTS,
      payload: sortOrder,
    });
  };

  const reorderDocument = (documentId: string, overDocumentId: string) => {
    dispatch({
      type: DocumentActionType.REORDER_DOCUMENT,
      payload: {
        documentId,
        overDocumentId,
      },
    });
  };

  const updateDocumentEngineMonitoring = (
    documentId: string,
    engineMonitoring: DocumentEngineMonitoring
  ) => {
    dispatch({
      type: DocumentActionType.UPDATE_DOCUMENT_ENGINE_MONITORING,
      payload: {
        documentId,
        engineMonitoring,
      },
    });
  };

  /*
  IMPORTANT: Don't use this function to update the content of the ACTIVE document.
  Use useActiveEditorView hook instead. It's unidirectional data flow.
  All changes in active document editor should be dispatched to the CodeMirror view.
   */
  const updateDocumentContent = async (
    documentId: string,
    editorState: EditorState
  ) => {
    const document = documentsState.documents.find(
      document => document.id === documentId
    );

    if (!document) {
      return;
    }

    await scriptsActions.updateScript(
      document.script.id,
      document.script.type,
      {
        editorState,
      }
    );
  };

  const renameDocument = async (documentId: string, newTitle: string) => {
    if (newTitle === "") {
      return;
    }

    const document = documentsState.documents.find(
      document => document.id === documentId
    );

    if (!document) {
      return;
    }

    await scriptsActions.updateScript(
      document.script.id,
      document.script.type,
      {
        title: newTitle,
      }
    );
  };

  const setActiveDocument = (documentId: string) => {
    dispatch({
      type: DocumentActionType.SET_ACTIVE_DOCUMENT,
      payload: documentId,
    });
  };

  const removeDocument = (documentId: string) => {
    const document = documentsState.documents.find(
      document => document.id === documentId
    );

    if (!document) {
      return;
    }

    document.execution?.queryStatements.forEach(queryStatement => {
      delete qsResults.current[queryStatement.id];
    });

    dispatch({
      type: DocumentActionType.REMOVE_DOCUMENT,
      payload: documentId,
    });

    scriptsActions.removeScript(document.script.id, document.script.type);
  };

  const removeAllDocuments = (exceptDocumentIds: string[]) => {
    documentsState.documents.forEach(document => {
      if (exceptDocumentIds.includes(document.id)) {
        return;
      }
      scriptsActions.removeScript(document.script.id, document.script.type);
    });

    dispatch({
      type: DocumentActionType.REMOVE_ALL_DOCUMENTS,
      payload: {
        exceptDocumentIds,
      },
    });
  };

  const changeDocumentContext = (
    documentId: string,
    context: Partial<ExecutionContext>
  ) => {
    dispatch({
      type: DocumentActionType.CHANGE_DOCUMENT_CONTEXT,
      payload: {
        documentId,
        context,
      },
    });
  };

  const cancelDocumentExecution = (documentId: string) => {
    dispatch({
      type: DocumentActionType.CANCEL_DOCUMENT_EXECUTION,
      payload: documentId,
    });
  };

  const updateDocumentCancellationStatus = (
    documentId: string,
    queryStatementId: string,
    status: CancellationStatus
  ) => {
    dispatch({
      type: DocumentActionType.UPDATE_DOCUMENT_EXECUTION_CANCELLATION_STATUS,
      payload: {
        documentId,
        queryStatementId,
        cancellationStatus: status,
      },
    });
  };

  const updateDocumentQueryStatement = (
    documentId: string,
    id: string,
    queryStatement: Partial<
      Omit<Omit<QueryStatement, "resultRows">, "prevStatus">
    >,
    result?: QueryStatementResult
  ) => {
    if (result) {
      const qsId = id;
      if (qsId) {
        qsResults.current[qsId] = result;
      }
    }

    dispatch({
      type: DocumentActionType.UPDATE_DOCUMENT_QUERY_STATEMENT,
      payload: {
        documentId,
        id,
        queryStatement: {
          ...queryStatement,
          resultRows: result?.rows,
          sortConfig: undefined,
          filters: undefined,
          interactionState: undefined,
        },
      },
    });
  };

  const switchDocumentActiveQueryStatement = ({
    documentId,
    queryStatementIndex,
    userSelectedActiveQueryStatementIndexTimestamp,
    activeOutputTab,
  }: SwitchDocumentActiveQueryStatementPayload) => {
    dispatch({
      type: DocumentActionType.SWITCH_DOCUMENT_ACTIVE_QUERY_STATEMENT,
      payload: {
        documentId,
        queryStatementIndex,
        userSelectedActiveQueryStatementIndexTimestamp,
        activeOutputTab,
      },
    });
  };

  const changeQueryStatementResultState = (data: Record<string, unknown>) => {
    const activeDocument = documentsState.documents.find(
      doc => doc.id === documentsState.activeDocumentId
    );
    if (!activeDocument?.execution) {
      return;
    }
    const { execution } = activeDocument;
    dispatch({
      type: DocumentActionType.SET_QUERY_STATEMENT_STATE,
      payload: {
        documentId: documentsState.activeDocumentId as string,
        queryStatementIndex: execution.activeQueryStatementIndex,
        data,
      },
    });
  };

  const switchActiveOutputTab = ({
    documentId,
    activeOutputTab,
  }: SwitchActiveOutputPayload) => {
    dispatch({
      type: DocumentActionType.SWITCH_ACTIVE_OUTPUT_TAB,
      payload: {
        documentId,
        activeOutputTab,
      },
    });
  };

  const updateDocumentAIState = useCallback(
    (documentId: string, newAiState: DocumentAiStateUpdate) => {
      dispatch({
        type: DocumentActionType.UPDATE_DOCUMENT_AI_STATE,
        payload: {
          documentId,
          newAiState,
        },
      });
    },
    [dispatch]
  );

  const addDocumentAIState = (
    documentId: string,
    aiState: DocumentAiStateInitialData
  ) => {
    dispatch({
      type: DocumentActionType.ADD_DOCUMENT_AI_STATE,
      payload: { documentId, aiState },
    });
  };

  const removeDocumentAIState = (documentId: string) => {
    dispatch({
      type: DocumentActionType.REMOVE_DOCUMENT_AI_STATE,
      payload: { documentId },
    });
  };

  const cancelAIOperation = (documentId: string) => {
    if (aiAbortControllerByDocumentId.current[documentId]) {
      aiAbortControllerByDocumentId.current[documentId]?.abort();
      aiAbortControllerByDocumentId.current[documentId] = null;
    }
    // Immediately clear any in-flight AI status text in the UI
    updateDocumentAIState(documentId, { aiResponse: null });
  };

  const performAIOperation = useCallback(
    async (
      documentId: string,
      prompt: string,
      onSuccess: (result: {
        modifiedSql: string | null;
        explanation: string;
      }) => void,
      metadata: Record<string, unknown>
    ) => {
      if (aiAbortControllerByDocumentId.current[documentId]) {
        console.warn("Operation already in progress for document", documentId);
        return;
      }

      updateDocumentAIState(documentId, {
        isLoading: true,
        lastError: null,
        aiResponse: null,
      });
      aiAbortControllerByDocumentId.current[documentId] = new AbortController();
      try {
        const result = await callA2ALLM({
          prompt,
          signal: aiAbortControllerByDocumentId.current[documentId]?.signal!,
          metadata,
          onStatusUpdate: (status: string) => {
            if (
              aiAbortControllerByDocumentId.current[documentId]?.signal?.aborted
            ) {
              return;
            }
            updateDocumentAIState(documentId, { aiResponse: status });
          },
        });
        onSuccess(result);
      } catch (error: any) {
        if (error.name === "AbortError" || error.name === "CanceledError") {
          return;
        }

        updateDocumentAIState(documentId, { lastError: error.message });
      } finally {
        updateDocumentAIState(documentId, { isLoading: false });
        aiAbortControllerByDocumentId.current[documentId] = null;
      }
    },
    [updateDocumentAIState]
  );

  const handleAIOperation = async (documentId: string, userPrompt: string) => {
    const document = documentsState.documents.find(
      doc => doc.id === documentId
    );
    if (!document) {
      return;
    }

    const aiState = document.aiState;

    if (!aiState) {
      return;
    }

    const sql = aiState.sql;
    const wrappedText = aiState.wrappedText;

    if (userPrompt && userPrompt.length > 1000) {
      putStatusMessage({
        message: `Prompt is too long (${userPrompt.length}/1000). Please shorten it to 1000 characters or less.`,
        type: StatusMessageType.Error,
        options: {
          id: "ai-prompt-too-long",
          insertToPosition: StatusMessagePosition.Top,
          autoRemove: false,
        },
      });
      return;
    }

    const script = getDocumentScript(document, scriptsState);
    if (!script || !script.editorState) {
      console.error("No script or editor state found for document");
      return;
    }

    const onSuccess = ({
      modifiedSql,
      explanation,
    }: {
      modifiedSql: string | null;
      explanation: string;
    }) => {
      // if newContent is the same as the original content, don't apply the change
      if (modifiedSql === aiState.sql) {
        updateDocumentAIState(documentId, {
          aiResponse: explanation || "No changes to apply.",
        });

        return;
      }

      updateDocumentAIState(documentId, { aiResponse: explanation || null });

      if (modifiedSql !== null) {
        const document = documentsStateRef.current.documents.find(
          doc => doc.id === documentId
        );
        if (!document) {
          return;
        }
        const script = getDocumentScript(document, scriptsStateRef.current);
        if (!script || !script.editorState) {
          console.error(
            "No script or editor state found for document (latest)"
          );
          return;
        }

        const fullText = script.editorState.doc;

        if (!document.aiState || !document.aiState.pos) {
          return;
        }

        const [start, end] = document.aiState.pos;

        // Apply change transactionally to preserve history
        let cmState: CMEditorState;
        try {
          cmState = CMEditorState.fromJSON(
            script.editorState,
            { extensions: [history()] },
            { history: historyField }
          );
        } catch (_err) {
          cmState = CMEditorState.create({
            doc: script.editorState.doc,
            extensions: [history()],
          });
        }

        const docLength = cmState.doc.length;
        const safeFrom = Math.max(0, Math.min(start, docLength));
        const safeTo = Math.max(safeFrom, Math.min(end, docLength));

        const tr = cmState.update({
          changes: { from: safeFrom, to: safeTo, insert: modifiedSql },
          selection: EditorSelection.single(safeFrom),
        });
        const json = tr.state.toJSON({ history: historyField });
        const nextEditorState: EditorState = {
          doc: json.doc,
          selection: json.selection,
          history: json.history,
          original: fullText.toString(),
        };

        updateDocumentContent(documentId, nextEditorState);
      }
    };

    const finalPrompt = generatePrompt({
      sql,
      userPrompt,
      ...(wrappedText ? { wrappedText } : {}),
    });

    // Normalize context values to avoid passing Symbols into string templates
    const dbNameRaw = document.context.database.name;
    const databaseName = typeof dbNameRaw === "string" ? dbNameRaw : undefined;

    const metadata = {
      accountName: account.accountName,
      catalogName: databaseName ?? undefined,
    };

    performAIOperation(documentId, finalPrompt, onSuccess, metadata);
  };

  useEffect(() => {
    documentsState.documents.forEach(document => {
      if (document.aiState && document.aiState.autoRun) {
        if (!document.aiState.text) {
          console.warn(
            "Auto-running AI operation with no text",
            document.aiState
          );
          return;
        }
        handleAIOperation(document.id, document.aiState.text);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- should run when documentsState changes
  }, [documentsState]);

  const fixQueryStatement = async (
    documentId: string,
    queryStatementId: string,
    errorMessage: string
  ) => {
    const document = documentsState.documents.find(
      doc => doc.id === documentId
    );
    if (!document) {
      return;
    }

    const script = getDocumentScript(document, scriptsState);
    const queryStatement = document.execution?.queryStatements.find(
      qs => qs.id === queryStatementId
    );

    if (!script || !queryStatement || !queryStatement.error) {
      return;
    }

    const {
      location: { start, end },
      content,
    } = queryStatement;

    removeDocumentAIState(documentId);

    addDocumentAIState(documentId, {
      text: buildFixUserPrompt(errorMessage),
      pos: [start, end],
      widgetPosition: start,
      sql: content,
      autoRun: true,
      externalInitializer: AiStateExternalInitializer.FixQueryStatement,
    });
  };

  // eslint-disable-next-line react/jsx-no-constructed-context-values -- it's ok here
  const contextValue: DocumentsContextType = {
    state: documentsState,
    qsResults: qsResults.current,
    shouldGenerateSampleScript,
    actions: {
      createDocument,
      removeDocument,
      setActiveDocument,
      renameDocument,
      updateDocumentContent,
      changeDocumentContext,
      startDocumentExecution,
      updateDocumentQueryStatement,
      cancelDocumentExecution,
      updateDocumentCancellationStatus,
      switchDocumentActiveQueryStatement,
      switchActiveOutputTab,
      changeDocumentLayout,
      removeAllDocuments,
      sortDocuments,
      updateDocumentEngineMonitoring,
      reorderDocument,
      addDocumentAIState,
      removeDocumentAIState,
      fixQueryStatement,
      updateDocumentAIState,
      handleAIOperation,
      cancelAIOperation,
      changeQueryStatementResultState,
    },
  };

  return (
    <DocumentsContext.Provider value={contextValue}>
      {children}
    </DocumentsContext.Provider>
  );
};
